import { ButtonText, Badge, SearchInput, Button } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { fetchInvitedWorkspaceUsers } from 'src/api/queries';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDeleteRight,
  faEnvelope,
  faSearch
} from '@fortawesome/pro-regular-svg-icons';
import SubmissionLoader from '../../Submissions/SubmissionLoader';
import { IInvitedUserSearchItem } from '@publishing-engine/domain/models';
import { formatDate } from 'src/utils/date';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

interface InvitedUsersPanelProps {
  workspaceId?: string;
}

interface IInvitedUserFilter {
  searchParams: string;
}

const defaultValues: IInvitedUserFilter = {
  searchParams: ''
};

const invitedUserStatusBadge = (
  <Badge size="context" variant="neutral" alignItems="center">
    <FontAwesomeIcon icon={faEnvelope} /> Invited
  </Badge>
);

const formatInvitedDate = (date?: string) =>
  date ? formatDate(new Date(date)) : '-';

const formatInvitedUserJournals = (user: IInvitedUserSearchItem) =>
  user.journals?.length
    ? user.journals.map(journal => journal.shortname.toUpperCase()).join(', ')
    : 'N/A';

const InvitedUsersPanel = ({
  workspaceId: providedWorkspaceId
}: InvitedUsersPanelProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const { workspaceId: fallbackWorkspaceId } = useWorkspace();
  const workspaceId = providedWorkspaceId ?? fallbackWorkspaceId;

  const { watch, resetField, reset, setValue } = useForm<IInvitedUserFilter>({
    defaultValues
  });

  const filters = watch();
  const offset = (page - 1) * pageSize;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invited-users-list', { workspaceId, filters, page, pageSize }],
    queryFn: () =>
      fetchInvitedWorkspaceUsers({
        workspaceId,
        limit: pageSize,
        offset,
        filters
      }),
    enabled: Boolean(workspaceId)
  });

  const clearFilters = () => {
    reset(defaultValues);
    setSearchValue('');
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        searchParams: (filters.searchParams ?? '').trim()
      }),
    [filters.searchParams]
  );

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  const totalUsers = data?.total ?? 0;
  const hasFilters = !!filters.searchParams;
  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, totalUsers);

  return (
    <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
      <Flex flexDirection="column" gap={4} width="100%">
        <Flex flexDirection="column" gap={2} mb={2}>
          <Flex gap={2} alignItems="center">
            <SearchInput
              placeholder="Search invited users by email"
              iconLeft={<FontAwesomeIcon icon={faSearch} />}
              width="100%"
              name="searchParams"
              aria-label="Search invited users"
              enterKeyHint="search"
              value={searchValue}
              onChange={e => setSearchValue(e.currentTarget.value)}
              onInputClear={() => {
                setSearchValue('');
                resetField('searchParams');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setValue('searchParams', e.currentTarget.value.trim());
                }
              }}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setValue('searchParams', e.currentTarget.value.trim());
                }
              }}
            />
            <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
              <Button
                variant="primary"
                aria-label="Search"
                onClick={() => {
                  setValue('searchParams', searchValue.trim());
                }}
              >
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Flex>
      {isLoading ? (
        <Text>Searching for matches...</Text>
      ) : (
        <Flex alignItems={'center'} gap={2} height={5}>
          <Text textStyle="textBody">
            {totalUsers > 0 ? (
              <>
                Showing <strong>{startIndex}</strong> -{' '}
                <strong>{endIndex}</strong> of <strong>{totalUsers}</strong>{' '}
                invited user
                {totalUsers === 1 ? '' : 's'}
              </>
            ) : (
              <>
                <strong>0</strong> invited users
              </>
            )}
          </Text>
          {hasFilters && (
            <ButtonText
              onClick={clearFilters}
              leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
              variant="secondary"
            >
              Clear filters
            </ButtonText>
          )}
        </Flex>
      )}

      <Box width="100%">
        {isLoading ? (
          <SubmissionLoader
            title="Loading Search Results"
            subTitle="We are retrieving your search results. This should only take a moment."
            bgColor="neutralSecondaryFill"
          />
        ) : data?.total ? (
          <Box overflowY="auto" width="100%">
            <Table size="sm" minWidth="100%">
              <Thead>
                <Tr
                  gap={2}
                  data-testid="invited-users-table-header"
                  sx={{
                    '& th': {
                      textTransform: 'none',
                      color: 'black',
                      background: 'white'
                    }
                  }}
                >
                  <Th p={4}>
                    <Text textStyle="headingItem">Email</Text>
                  </Th>
                  <Th p={4}>
                    <Text textStyle="headingItem">Journals</Text>
                  </Th>
                  <Th p={4}>
                    <Text textStyle="headingItem">Status</Text>
                  </Th>
                  <Th p={4}>
                    <Text textStyle="headingItem">Invited Via</Text>
                  </Th>
                  <Th p={4}>
                    <Text textStyle="headingItem">Invited Date</Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.items.map((user: IInvitedUserSearchItem) => (
                  <Tr
                    key={`${user.email}-${user.invitedDate}`}
                    data-testid="invited-users-table-row"
                    background="white"
                  >
                    <Td p={4}>
                      <Text>{user.email}</Text>
                    </Td>
                    <Td p={4}>
                      <Text>{formatInvitedUserJournals(user)}</Text>
                    </Td>
                    <Td p={4}>{invitedUserStatusBadge}</Td>
                    <Td p={4}>
                      <Text>{user.invitedVia}</Text>
                    </Td>
                    <Td p={4}>
                      <Text>{formatInvitedDate(user.invitedDate)}</Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : isError ? (
          <SubmissionLoader
            title="Search Failed"
            subTitle="We encountered an issue connecting to the search service. Please check your connection or try again later."
            bgColor="errorPrimaryFill"
          />
        ) : (
          <SubmissionLoader
            title="No Results Found"
            subTitle="We could not find any invited users matching your search criteria. Please adjust your search and try again."
            bgColor="neutralSecondaryFill"
          />
        )}
        <TablePagination
          ariaLabel="Invited users pagination"
          currentPage={page}
          totalItems={totalUsers}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Box>
    </Flex>
  );
};

export default InvitedUsersPanel;
